/**
 * Settlement result UI (#788): the portfolio "Settled" panel, the trade-history annotation and the result banner
 * on a settled market's page. Data from useSettlements / useSettledOutcome (HTTP /info, cached per wallet).
 */
import { Link } from 'react-router-dom';
import { useGhost } from '../wallet/context';
import { formatShares } from '../market/format';
import type { MarketResult, SettlementOutcomeKind, SettlementResult, SettlementSummary } from './parse';
import { settlementSummary, signedUsd2, usd2 } from './parse';
import { FILLS_PAGE_CAP } from './store';
import { useSettlements } from './useSettlements';
import type { PlainKey } from '../i18n/i18n';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

const RESULT_LABEL: Record<SettlementOutcomeKind, PlainKey> = {
  won: 'settlement.result.won',
  lost: 'settlement.result.lost',
  partial: 'settlement.result.partial',
};
const RESULT_CLASS: Record<SettlementOutcomeKind, string> = { won: 'text-yes', lost: 'text-no', partial: 'text-warn' };

function fmtSettledTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Amount in its fee token: "$20.00" for USDC, "20.00 USDH" otherwise. */
function money(v: string, token: string): string {
  return token === 'USDC' ? usd2(v) : `${usd2(v).replace('$', '')} ${token}`;
}

/** Signed amount in its fee token: "+$12.34", "-$3.21", "$0.00"; "+12.34 USDH" for other tokens. */
function signedMoney(v: string, token: string): string {
  const s = signedUsd2(v);
  return token === 'USDC' ? s : `${s.replace('$', '')} ${token}`;
}

/** Green for a gain, red for a loss, neutral for zero (by the rounded amount shown). */
function profitClass(shown: string): string {
  return shown.startsWith('+') ? 'text-yes' : shown.startsWith('-') ? 'text-no' : '';
}

/** Market link that keeps ?ghost= and opens the side that was held. */
function useMarketHref(): (outcome: number, side?: number) => string {
  const { ghostAddress } = useGhost();
  return (outcome, side) => {
    const p = new URLSearchParams();
    if (side !== undefined) p.set('side', String(side));
    if (ghostAddress) p.set('ghost', ghostAddress);
    const s = p.toString();
    return `/market/${outcome}${s ? `?${s}` : ''}`;
  };
}

export function ResultBadge({ result }: { result: SettlementOutcomeKind }) {
  const t = useT();
  return (
    <span className={`font-semibold ${RESULT_CLASS[result]}`} data-testid="settlement-result">
      {t(RESULT_LABEL[result])}
    </span>
  );
}

function CorrectedNote() {
  const t = useT();
  return (
    <div className="text-xs text-warn" data-testid="settlement-corrected">
      {t('settlement.corrected')}
    </div>
  );
}

const th = 'px-3 py-2 text-start font-medium whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap align-top';

export function SettledTable({ rows }: { rows: readonly SettlementResult[] }) {
  const href = useMarketHref();
  const t = useT();
  const head: PlainKey[] = [
    'settlement.col.settled',
    'common.market',
    'settlement.col.side',
    'settlement.col.result',
    'settlement.col.payout',
    'common.shares',
    'settlement.col.total',
    'settlement.col.profit',
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm tabular-nums">
        <thead className="text-xs text-muted">
          <tr>
            {head.map((h) => (
              <th
                key={h}
                className={th}
                scope="col"
                {...(h === 'settlement.col.profit' ? { title: t('settlement.profit_note'), 'aria-describedby': 'settled-profit-note' } : {})}
              >
                {t(h)}
                {h === 'settlement.col.profit' && <sup aria-hidden="true">*</sup>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.tid} data-testid="settled-row" data-outcome={r.outcome}>
              <td className={td}>{fmtSettledTime(r.time)}</td>
              <td className="max-w-[22rem] px-3 py-2 align-top">
                <Link
                  to={href(r.outcome, r.side)}
                  className="block truncate font-medium hover:underline"
                  data-testid="settled-title"
                  translate="no"
                >
                  {r.title}
                </Link>
                <div className="truncate text-xs text-muted" translate="no">
                  {r.resultText}
                </div>
                {r.corrected && <CorrectedNote />}
              </td>
              <td className={`${td} ${r.side === 0 ? 'text-yes' : 'text-no'}`} data-testid="settled-side" translate="no">
                {r.sideName}
              </td>
              <td className={td}>
                <ResultBadge result={r.result} />
              </td>
              <td className={td} data-testid="settled-payout">
                {money(r.payoutPerShare, r.feeToken)}
              </td>
              <td className={td}>{formatShares(r.shares)}</td>
              <td className={td}>
                <span data-testid="settled-total">{money(r.net, r.feeToken)}</span>
                {Number(r.fee) > 0 && <div className="text-xs text-muted">{t('settlement.after_fee', { fee: money(r.fee, r.feeToken) })}</div>}
              </td>
              <ProfitCell profit={r.profit} token={r.feeToken} />
            </tr>
          ))}
        </tbody>
      </table>
      <p id="settled-profit-note" className="px-4 py-2 text-xs text-muted" data-testid="settled-profit-note">
        * {t('settlement.profit_note')}
      </p>
    </div>
  );
}

function ProfitCell({ profit, token }: { profit: string | null; token: string }) {
  if (profit === null) {
    return (
      <td className={`${td} text-muted`} data-testid="settled-profit" data-profit="">
        —
      </td>
    );
  }
  const shown = signedMoney(profit, token);
  return (
    <td className={`${td} font-medium ${profitClass(shown)}`} data-testid="settled-profit" data-profit={profit}>
      {shown}
    </td>
  );
}

/** Summary line above the Settled table: markets, won / lost (/ partial), total profit; the reach note. */
export function SettledSummary({ summary }: { summary: SettlementSummary }) {
  const t = useT();
  const total = summary.profit === null ? null : signedUsd2(summary.profit);
  return (
    <div className="space-y-1 border-b border-border px-4 py-2 text-sm" data-testid="settled-summary">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span data-testid="settled-summary-markets">{t('settlement.summary.markets', { count: summary.markets })}</span>
        <span className="text-yes" data-testid="settled-summary-won">
          {t('settlement.summary.won', { count: summary.won })}
        </span>
        <span className="text-no" data-testid="settled-summary-lost">
          {t('settlement.summary.lost', { count: summary.lost })}
        </span>
        {summary.partial > 0 && (
          <span className="text-warn" data-testid="settled-summary-partial">
            {t('settlement.summary.partial', { count: summary.partial })}
          </span>
        )}
        <span className="font-semibold" data-testid="settled-summary-profit" data-profit={summary.profit ?? ''}>
          <Msg
            k="settlement.summary.profit"
            args={{ amount: <span className={total ? profitClass(total) : 'text-muted'}>{total ?? '—'}</span> }}
          />
        </span>
        {summary.excluded > 0 && (
          <span className="text-xs text-muted" data-testid="settled-summary-excluded">
            {t('settlement.summary.excluded', { count: summary.excluded })}
          </span>
        )}
      </div>
      {summary.truncated && (
        <div className="text-xs" role="note" data-testid="settled-truncated">
          <div className="font-medium">
            <span aria-hidden="true">ⓘ </span>
            {t('settlement.truncated')}
          </div>
          <div className="text-muted" data-testid="settled-truncated-info">
            {t('settlement.truncated_info', { limit: FILLS_PAGE_CAP })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Portfolio "Settled" tab. */
export function SettledPanel({ address }: { address: string }) {
  const s = useSettlements(address);
  const t = useT();
  return (
    <section className="card overflow-hidden" aria-label={t('settlement.title')} data-testid="settled-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">
          {t('settlement.title')} <span className="ms-1 text-muted">({s.results.length})</span>
        </h2>
        <span className="text-xs text-muted">
          {s.refreshing ? t('settlement.checking') : t('settlement.week')}
        </span>
      </div>
      {s.error && (
        <p className="px-4 py-2 text-xs text-danger" role="status">
          {t('settlement.error', { error: s.error })}{' '}
          <button type="button" className="text-accent hover:underline" onClick={s.refresh}>
            {t('common.retry')}
          </button>
        </p>
      )}
      {s.loading ? (
        <p className="px-4 py-6 text-center text-sm text-muted">{t('settlement.loading')}</p>
      ) : s.results.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted" data-testid="settled-empty">
          {t('settlement.empty')}
        </p>
      ) : (
        <>
          <SettledSummary summary={settlementSummary(s.results, s.truncated)} />
          <SettledTable rows={s.results} />
        </>
      )}
    </section>
  );
}

/** Extra line under a Settlement fill in trade history. */
export function SettlementFillNote({ r }: { r: SettlementResult }) {
  const href = useMarketHref();
  const t = useT();
  return (
    <div className="text-xs" data-testid="fill-settlement">
      <ResultBadge result={r.result} />{' '}
      <span className="text-muted">· {t('settlement.per_share', { amount: money(r.payoutPerShare, r.feeToken) })} ·</span>{' '}
      <Link to={href(r.outcome, r.side)} className="text-accent hover:underline" translate="no">
        {r.resultText}
      </Link>
      {r.corrected && <CorrectedNote />}
    </div>
  );
}

/** Banner on a settled market's page: the market result, and the viewed wallet's own payout if it held it. */
export function SettlementBanner({ market, mine }: { market: MarketResult; mine: readonly SettlementResult[] }) {
  const t = useT();
  return (
    <div className="card space-y-2 border-accent/50 p-4" role="status" data-testid="settlement-banner">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
          {t('settlement.badge')}
        </span>
        <span className="font-semibold" data-testid="settlement-banner-result" translate="no">
          {market.resultText}
        </span>
      </div>
      {market.details && (
        <p className="text-sm text-muted" data-testid="settlement-banner-details" translate="no">
          {market.details}
        </p>
      )}
      {mine.map((r) => (
        <p key={r.tid} className="text-sm" data-testid="settlement-banner-mine">
          <Msg
            k="settlement.mine"
            args={{
              shares: formatShares(r.shares),
              side: <span translate="no">{r.sideName}</span>,
              result: <ResultBadge result={r.result} />,
              net: money(r.net, r.feeToken),
              perShare: money(r.payoutPerShare, r.feeToken),
              time: fmtSettledTime(r.time),
            }}
          />
          {r.corrected && <CorrectedNote />}
        </p>
      ))}
    </div>
  );
}
